import { fbt } from 'fbtee'
import { ChevronRight, MoreHorizontal, Unlink, Users } from 'lucide-react'
import { GroupFaceStack } from '../../components/GroupFaceStack'
import { ThumbStack } from '../../components/profile/ThumbStack'
import { Button } from '../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import { clickableProps } from '../../lib/a11y'
import { memberCountLabel } from '../../lib/commonStrings'
import type { Group, UserProfile } from '../../types'

function plansLine(going: number, interested: number, members: number): string {
  if (going > 0 && interested > 0) {
    return fbt(
      fbt.param('going', going) + ' going · ' + fbt.param('interested', interested) + ' interested',
      'Linked group row subtitle on the event page: how many members are going and how many are interested',
    )
  }
  if (going > 0) {
    return fbt(
      fbt.param('going', going) + ' going',
      'Linked group row subtitle on the event page: how many members are going',
    )
  }
  if (interested > 0) {
    return fbt(
      fbt.param('interested', interested) + ' interested',
      'Linked group row subtitle on the event page: how many members are interested',
    )
  }
  return memberCountLabel(members)
}

/** A group linked to the event: cover, name, who in it is in, faces, and unlink behind ⋯. */
export function LinkedGroupRow({
  group,
  goingIds,
  interestedCount,
  profiles,
  onOpen,
  onUnlink,
}: {
  group: Group
  goingIds: ReadonlySet<string>
  interestedCount: number
  profiles: Record<string, UserProfile>
  onOpen: () => void
  /** Omit for guests — unlinking is a member-only action. */
  onUnlink?: () => void
}) {
  return (
    <div className="flex items-center rounded-lg hover:bg-muted" data-testid="linked-group-row">
      <div
        {...clickableProps(onOpen)}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 px-2 py-2.5 text-left"
      >
        <ThumbStack srcs={[group.imageUrl]} icon={Users} size="md" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{group.name}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {plansLine(goingIds.size, interestedCount, group.memberIds.length)}
          </span>
        </span>
        <GroupFaceStack
          memberIds={group.memberIds}
          first={goingIds}
          profiles={profiles}
          dialogTitle={group.name}
          historyKey={`linked-group:${group.id}`}
          showGoing
        />
        {!onUnlink && <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
      </div>
      {onUnlink && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="mr-1 size-8 text-muted-foreground"
              aria-label={fbt(
                'Options for ' + fbt.param('group', group.name),
                'Accessible label for the ⋯ menu on a group linked to the event',
              )}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem variant="destructive" onSelect={onUnlink}>
              <Unlink />
              {fbt('Unlink from this event', 'Menu item that unlinks a group from the event on the event page')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
